import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Close, Search, Store } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { useEffect, useMemo, useState } from "react"
import { copiarCategoriaParaLojas } from "../../../services/cardapio"
import { RegistrarLogErro } from "../../../services/clientefiel"
import { buscarEstabelecimentosPaginado } from "../../../services/integracao"
import useDebounce from "../../../utils/useDebounce"

const useStyles = makeStyles(theme => ({
    listaContainer: {
        position: "relative",
        minHeight: 260,
        maxHeight: 360,
        overflowY: "auto",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
    },
    overlay: {
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(255,255,255,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1,
    },
    item: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    iconeLoja: {
        color: theme.palette.text.secondary,
        marginRight: 8,
    },
    selecionadasBox: {
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 8,
        minHeight: 8,
    },
}))

const PAGE_SIZE = 20

export default function DialogCopiarCategoria({
    open,
    onClose,
    categoria,
    idEstabelecimentoAtual,
}) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const autorizacao = localStorage.getItem("chaveSistema")

    const [q, setQ] = useState("")
    const qDebounced = useDebounce(q, 300)
    const [page, setPage] = useState(1)

    const [estabelecimentos, setEstabelecimentos] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [erro, setErro] = useState("")

    // Mapa id -> { id, nome } das lojas escolhidas (mantém seleção entre buscas/páginas).
    const [selecionadas, setSelecionadas] = useState({})
    const [copiando, setCopiando] = useState(false)
    const [confirmando, setConfirmando] = useState(false)

    const totalPaginas = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])
    const idsSelecionadas = useMemo(() => Object.keys(selecionadas), [selecionadas])

    const buscar = async (pageAtual = page) => {
        setLoading(true)
        setErro("")
        const resp = await buscarEstabelecimentosPaginado(
            { q: qDebounced, apenasAtivos: true, page: pageAtual, size: PAGE_SIZE },
            autorizacao
        )
        setLoading(false)
        if (resp.retornoErro) {
            setErro(resp.mensagem || "Erro ao buscar lojas.")
            setEstabelecimentos([])
            setTotal(0)
            return
        }
        setEstabelecimentos(resp.estabelecimentos)
        setTotal(resp.total)
    }

    useEffect(() => {
        if (open) {
            setPage(1)
            buscar(1)
        } else {
            setQ("")
            setPage(1)
            setEstabelecimentos([])
            setTotal(0)
            setErro("")
            setSelecionadas({})
            setConfirmando(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
        if (!open) return
        setPage(1)
        buscar(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qDebounced])

    const handleTrocarPagina = nova => {
        setPage(nova)
        buscar(nova)
    }

    const toggleLoja = est => {
        setSelecionadas(prev => {
            const novo = { ...prev }
            if (novo[est.id]) delete novo[est.id]
            else novo[est.id] = { id: est.id, nome: est.nome }
            return novo
        })
    }

    const handleCopiar = async () => {
        if (idsSelecionadas.length === 0) return
        setCopiando(true)
        try {
            const resp = await copiarCategoriaParaLojas(
                categoria?.id,
                idsSelecionadas.map(id => Number(id)),
                autorizacao
            )
            if (resp?.retornoErro) {
                enqueueSnackbar(resp.mensagem || "Não foi possível copiar a categoria.", {
                    variant: "error",
                })
                return
            }
            const copiadas = resp?.copiadas ?? idsSelecionadas.length
            enqueueSnackbar(
                `Categoria "${categoria?.nome}" copiada para ${copiadas} loja(s).`,
                { variant: "success" }
            )
            if (Array.isArray(resp?.erros)) {
                resp.erros.forEach(e =>
                    enqueueSnackbar(`${e.nome || e.idLoja}: ${e.mensagem}`, { variant: "warning" })
                )
            }
            onClose?.()
        } catch (error) {
            RegistrarLogErro(error, "DialogCopiarCategoria.handleCopiar", {
                idCategoria: categoria?.id,
            })
            enqueueSnackbar("Erro inesperado ao copiar a categoria.", { variant: "error" })
        } finally {
            setCopiando(false)
        }
    }

    const inicio = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
    const fim = total === 0 ? 0 : Math.min(total, (page - 1) * PAGE_SIZE + estabelecimentos.length)

    return (
        <Dialog open={open} onClose={() => !copiando && onClose?.()} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        Copiar categoria para outras lojas
                    </Typography>
                    <IconButton size="small" onClick={onClose} disabled={copiando}>
                        <Close />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="textSecondary">
                    Categoria: <strong>{categoria?.nome}</strong>
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {!confirmando ? (
                    <>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Buscar loja por nome"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {idsSelecionadas.length > 0 && (
                            <Box className={classes.selecionadasBox}>
                                {Object.values(selecionadas).map(l => (
                                    <Chip
                                        key={l.id}
                                        size="small"
                                        label={l.nome}
                                        onDelete={() =>
                                            toggleLoja({ id: l.id, nome: l.nome })
                                        }
                                        style={{ backgroundColor: "#ede9fe", color: "#5b21b6" }}
                                    />
                                ))}
                            </Box>
                        )}

                        <Box className={classes.listaContainer} mt={1}>
                            {loading && (
                                <Box className={classes.overlay}>
                                    <CircularProgress size={32} />
                                </Box>
                            )}
                            {!loading && erro && (
                                <Box p={2}>
                                    <Typography variant="body2" style={{ color: "#611a15" }}>
                                        {erro}
                                    </Typography>
                                    <Box mt={1}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            onClick={() => buscar(page)}
                                        >
                                            Tentar novamente
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                            {!loading && !erro && estabelecimentos.length === 0 && (
                                <Box p={3}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        align="center"
                                    >
                                        Nenhuma loja encontrada para esta busca.
                                    </Typography>
                                </Box>
                            )}
                            {!erro && estabelecimentos.length > 0 && (
                                <List dense disablePadding>
                                    {estabelecimentos.map(est => {
                                        const ehAtual = est.id === idEstabelecimentoAtual
                                        const marcada = Boolean(selecionadas[est.id])
                                        return (
                                            <ListItem
                                                key={est.id}
                                                className={classes.item}
                                                disabled={ehAtual}
                                                onClick={() => !ehAtual && toggleLoja(est)}
                                            >
                                                <Checkbox
                                                    edge="start"
                                                    checked={marcada}
                                                    disabled={ehAtual}
                                                    tabIndex={-1}
                                                    size="small"
                                                />
                                                <Store className={classes.iconeLoja} />
                                                <ListItemText
                                                    primary={
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            style={{ gap: 8 }}
                                                        >
                                                            {est.id != null && (
                                                                <Chip
                                                                    size="small"
                                                                    label={est.id}
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            <span>{est.nome}</span>
                                                            {ehAtual && (
                                                                <Chip
                                                                    size="small"
                                                                    label="Loja atual"
                                                                    style={{
                                                                        backgroundColor: "#9e9e9e",
                                                                        color: "white",
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={est.nomeRede || ""}
                                                />
                                            </ListItem>
                                        )
                                    })}
                                </List>
                            )}
                        </Box>

                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            mt={1}
                            style={{ gap: 8 }}
                        >
                            <Typography variant="caption" color="textSecondary">
                                {total === 0 ? "—" : `Exibindo ${inicio}–${fim} de ${total}`}
                            </Typography>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={page <= 1 || loading}
                                    onClick={() => handleTrocarPagina(page - 1)}
                                >
                                    Anterior
                                </Button>
                                <Typography variant="body2">
                                    Página {page} de {totalPaginas}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={page >= totalPaginas || loading}
                                    onClick={() => handleTrocarPagina(page + 1)}
                                >
                                    Próximo
                                </Button>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <Box>
                        <Typography>
                            A categoria <strong>{categoria?.nome}</strong> será copiada para as
                            seguintes <strong>{idsSelecionadas.length}</strong> loja(s):
                        </Typography>
                        <Divider style={{ margin: "12px 0" }} />
                        <Box className={classes.selecionadasBox}>
                            {Object.values(selecionadas).map(l => (
                                <Chip
                                    key={l.id}
                                    size="small"
                                    label={l.nome}
                                    style={{ backgroundColor: "#ede9fe", color: "#5b21b6" }}
                                />
                            ))}
                        </Box>
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                            Uma cópia independente será criada em cada loja. Isso não altera a
                            categoria original.
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {!confirmando ? (
                    <>
                        <Button onClick={onClose} disabled={copiando}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            style={{
                                backgroundColor: idsSelecionadas.length ? "#7c3aed" : undefined,
                                color: idsSelecionadas.length ? "white" : undefined,
                            }}
                            disabled={idsSelecionadas.length === 0}
                            onClick={() => setConfirmando(true)}
                        >
                            Continuar ({idsSelecionadas.length})
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setConfirmando(false)} disabled={copiando}>
                            Voltar
                        </Button>
                        <Button
                            variant="contained"
                            style={{ backgroundColor: "#14A44D", color: "white" }}
                            disabled={copiando}
                            onClick={handleCopiar}
                            startIcon={
                                copiando ? <CircularProgress size={16} color="inherit" /> : null
                            }
                        >
                            {copiando ? "Copiando..." : "Confirmar cópia"}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}
